import { useState, useCallback } from 'react';
import { jqlString } from '@/lib/jql.js';

export interface FilterState {
  statuses: string[];
  assignees: string[];
  sortField: string;
  sortDir: 'ASC' | 'DESC';
  showDone: boolean;
  /** Only issues with a blocker that is not done */
  blockedOnly: boolean;
}

interface IssueFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  statuses: string[];
  assignees: string[];
}

const SORT_OPTIONS = [
  { value: 'updated', label: 'Updated' },
  { value: 'created', label: 'Created' },
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
  { value: 'key', label: 'Key' },
  { value: 'summary', label: 'Summary' },
  { value: 'assignee', label: 'Assignee' },
];

// Shared styles
const BTN = 'flex h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm shadow-sm transition-colors hover:bg-gray-50';
const CHECKBOX = 'h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 pointer-events-none';

// ── Multi-Select Dropdown ────────────────────────────────────────────

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  extraOptions?: Array<{ value: string; label: string }>;
}

function MultiSelect({ label, options, selected, onChange, extraOptions }: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  const display = selected.length === 0
    ? 'All'
    : selected.length === 1
      ? (extraOptions?.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`;

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5">
        <label className="text-xs font-medium text-gray-500">{label}</label>
        <button type="button" onClick={() => setOpen((v) => !v)} className={BTN} aria-expanded={open}>
          <span className={selected.length > 0 ? 'text-blue-700' : 'text-gray-600'}>{display}</span>
          <svg className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {open && (
        // Transparent backdrop closes the menu on any outside click (no document listeners to leak)
        <button type="button" className="fixed inset-0 z-20 cursor-default" aria-label={`Close ${label} filter`} onClick={() => setOpen(false)} tabIndex={-1} />
      )}
      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {selected.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full border-b border-gray-100 px-3 py-1.5 text-left text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Clear all
            </button>
          )}
          {extraOptions?.map((opt) => (
            <div
              key={opt.value}
              onClick={() => toggle(opt.value)}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50"
            >
              <input type="checkbox" checked={selected.includes(opt.value)} readOnly className={CHECKBOX} />
              <span className="text-gray-700">{opt.label}</span>
            </div>
          ))}
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => toggle(opt)}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50"
            >
              <input type="checkbox" checked={selected.includes(opt)} readOnly className={CHECKBOX} />
              <span className="text-gray-700">{opt}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Filter Bar ──────────────────────────────────────────────────

export function IssueFilters({ filters, onFilterChange, statuses, assignees }: IssueFiltersProps) {
  const update = useCallback(
    (partial: Partial<FilterState>) => {
      onFilterChange({ ...filters, ...partial });
    },
    [filters, onFilterChange],
  );

  const hasFilters = filters.statuses.length > 0 || filters.assignees.length > 0 || filters.blockedOnly;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <MultiSelect
        label="Status"
        options={statuses}
        selected={filters.statuses}
        onChange={(statuses) => update({ statuses })}
      />

      <MultiSelect
        label="Assignee"
        options={assignees}
        selected={filters.assignees}
        onChange={(assignees) => update({ assignees })}
        extraOptions={[{ value: 'UNASSIGNED', label: 'Unassigned' }]}
      />

      {/* Sort */}
      <div className="flex items-center gap-1.5">
        <label className="text-xs font-medium text-gray-500">Sort</label>
        <select
          value={filters.sortField}
          onChange={(e) => update({ sortField: e.target.value })}
          className={`${BTN} appearance-none pr-7 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22%239ca3af%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20d%3D%22M7%2010l5%205%205-5z%22/%3E%3C/svg%3E')] bg-[length:16px] bg-[right_6px_center] bg-no-repeat`}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          onClick={() => update({ sortDir: filters.sortDir === 'ASC' ? 'DESC' : 'ASC' })}
          className={`${BTN.replace('px-3', 'px-0')} w-8 shrink-0 justify-center`}
          title={filters.sortDir === 'ASC' ? 'Ascending' : 'Descending'}
          aria-label={filters.sortDir === 'ASC' ? 'Sort ascending' : 'Sort descending'}
        >
          <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            {filters.sortDir === 'ASC' ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 13V3M4 7l4-4 4 4" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 3v10M4 9l4 4 4-4" />
            )}
          </svg>
        </button>
      </div>

      {/* Show Done/Cancelled toggle */}
      <label className="flex h-8 cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 shadow-sm transition-colors hover:bg-gray-50">
        <input
          type="checkbox"
          checked={filters.showDone}
          onChange={(e) => update({ showDone: e.target.checked })}
          className={CHECKBOX}
          style={{ pointerEvents: 'auto' }}
        />
        <span className="text-sm text-gray-600">Show Done/Cancelled</span>
      </label>

      {/* Blocked only */}
      <label className={`flex h-8 cursor-pointer items-center gap-2 rounded-md border px-3 shadow-sm transition-colors ${filters.blockedOnly ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white hover:bg-gray-50'}`}>
        <input
          type="checkbox"
          checked={filters.blockedOnly}
          onChange={(e) => update({ blockedOnly: e.target.checked })}
          className={CHECKBOX}
          style={{ pointerEvents: 'auto' }}
        />
        <span className={`text-sm ${filters.blockedOnly ? 'font-medium text-red-700' : 'text-gray-600'}`}>Blocked</span>
      </label>

      {/* Clear */}
      {hasFilters && (
        <button
          onClick={() => onFilterChange({ ...filters, statuses: [], assignees: [], blockedOnly: false })}
          className="flex h-8 items-center rounded-md border border-red-200 bg-red-50 px-3 text-sm font-medium text-red-600 shadow-sm transition-colors hover:bg-red-100"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

export const EXCLUDED_STATUSES_DEFAULT = ['Done', 'Cancelled', 'Canceled', 'Closed'];

/**
 * Build JQL from a base project clause + filter state.
 */
export function buildJql(base: string, filters: FilterState, excludeDone = true): string {
  const clauses = [base];

  if (filters.statuses.length > 0) {
    clauses.push(`status IN (${filters.statuses.map(jqlString).join(', ')})`);
  } else if (excludeDone && !filters.showDone) {
    clauses.push(`status NOT IN (${EXCLUDED_STATUSES_DEFAULT.map(jqlString).join(', ')})`);
  }

  if (filters.assignees.length > 0) {
    const unassigned = filters.assignees.includes('UNASSIGNED');
    const named = filters.assignees.filter((a) => a !== 'UNASSIGNED');

    if (unassigned && named.length > 0) {
      clauses.push(`(assignee is EMPTY OR assignee IN (${named.map(jqlString).join(', ')}))`);
    } else if (unassigned) {
      clauses.push('assignee is EMPTY');
    } else {
      clauses.push(`assignee IN (${named.map(jqlString).join(', ')})`);
    }
  }

  const where = clauses.filter(Boolean).join(' AND ');
  return `${where} ORDER BY ${filters.sortField} ${filters.sortDir}`;
}

/**
 * Hook to manage filter state.
 */
export function useFilterState(defaultSort = 'updated', defaultDir: 'ASC' | 'DESC' = 'DESC', defaultShowDone = false) {
  const [filters, setFilters] = useState<FilterState>({
    statuses: [],
    assignees: [],
    sortField: defaultSort,
    sortDir: defaultDir,
    showDone: defaultShowDone,
    blockedOnly: false,
  });

  return { filters, setFilters };
}

/**
 * Extract unique statuses and assignees from issue results for filter dropdowns.
 */
export function extractFilterOptions(issues: Array<{ status: { name: string }; assignee: { displayName: string } | null }>) {
  const statusSet = new Set<string>();
  const assigneeSet = new Set<string>();

  for (const issue of issues) {
    if (issue.status?.name) statusSet.add(issue.status.name);
    if (issue.assignee?.displayName) assigneeSet.add(issue.assignee.displayName);
  }

  return {
    statuses: Array.from(statusSet).sort(),
    assignees: Array.from(assigneeSet).sort(),
  };
}
